/** biome-ignore-all lint/suspicious/noArrayIndexKey: temporarily disabled */
/** biome-ignore-all assist/source/organizeImports: temporarily disabled */
import { DatePicker, parseDate, type DateValue } from '@ark-ui/react/date-picker';
import { CalendarX, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import type { EmployeeLoggedStatus } from '../data/databaseSchema';
import { useCalendarInteractions } from '../hooks/useCalendarInteractions';
import { colors } from '../theme/colors';
import { getIsoWeek, getCalendarWeek } from '../utils/dateUtils';
import { CalendarCell } from './CalendarCell';
import { CalendarHeader } from './CalendarHeader';
import { CalendarLegend } from './CalendarLegend';

interface CalendarProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  monthlyStatus: EmployeeLoggedStatus[];
  onMonthChange?: (month: number, year: number) => void;
  weekRecord?: Record<string, string>;
  savedNavigation?: DateValue | null;
  onSaveNavigation?: (value: DateValue | null) => void;
  onOpenAbsenceVacationDialog?: () => void;
  onOpenExternalPresence?: () => void;
}

interface MonthChangeWatcherProps {
  visibleStartMonth: number;
  visibleStartYear: number;
  onMonthChange?: (month: number, year: number) => void;
}

const MonthChangeWatcher: React.FC<MonthChangeWatcherProps> = ({
  visibleStartMonth,
  visibleStartYear,
  onMonthChange,
}) => {
  React.useEffect(() => {
    if (onMonthChange) {
      onMonthChange(visibleStartMonth, visibleStartYear);
    }
  }, [visibleStartMonth, visibleStartYear, onMonthChange]);

  return null;
};

export const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  onDateSelect,
  monthlyStatus,
  onMonthChange,
  weekRecord = {},
  savedNavigation: savedNavigationProp,
  onSaveNavigation,
  onOpenAbsenceVacationDialog,
  onOpenExternalPresence,
}) => {
  const { hoveredDate, handleDateHover, handleDateLeave, resetHover } = useCalendarInteractions();

  // Track the focused/visible month independently of selectedDate
  const [focusedValue, setFocusedValue] = useState<DateValue | undefined>(undefined);

  // Use ref to track the last user navigation to prevent reset overwrites
  // Also use prop from parent to persist across remounts
  const lastUserNavigationRef = useRef<DateValue | null>(savedNavigationProp || null);

  // Sync ref with prop when prop changes
  useEffect(() => {
    if (savedNavigationProp) {
      lastUserNavigationRef.current = savedNavigationProp;
      // Update focusedValue if it's different from the prop
      if (
        !focusedValue ||
        focusedValue.month !== savedNavigationProp.month ||
        focusedValue.year !== savedNavigationProp.year
      ) {
        setFocusedValue(savedNavigationProp);
      }
    }
  }, [savedNavigationProp, focusedValue]);

  // Helper to get saved navigation (ref or prop fallback)
  const getSavedNavigation = useCallback((): DateValue | null => {
    return lastUserNavigationRef.current || savedNavigationProp || null;
  }, [savedNavigationProp]);

  // Helper to save navigation (update ref and parent)
  const saveNavigation = useCallback(
    (value: DateValue) => {
      lastUserNavigationRef.current = value;
      if (onSaveNavigation) {
        onSaveNavigation(value);
      }
    },
    [onSaveNavigation]
  );

  // Helper to check if month/year matches selected date
  const isSelectedDateMonth = useCallback(
    (month: number, year: number): boolean => {
      const selectedDateMonth = selectedDate.getMonth() + 1;
      const selectedDateYear = selectedDate.getFullYear();
      return month === selectedDateMonth && year === selectedDateYear;
    },
    [selectedDate]
  );

  // Helper to restore focusedValue from saved navigation
  const restoreFocusedValue = useCallback(
    (savedNav: DateValue) => {
      saveNavigation(savedNav);
      setFocusedValue(savedNav);
    },
    [saveNavigation]
  );

  const statusMap = React.useMemo(() => {
    const map = new Map<string, EmployeeLoggedStatus>();
    monthlyStatus.forEach((status) => {
      map.set(status.date_of_year, status);
    });
    return map;
  }, [monthlyStatus]);

  const getStatusForDate = React.useCallback(
    (dateString: string): EmployeeLoggedStatus => {
      const status = statusMap.get(dateString);
      if (status) {
        return status;
      }

      return {
        employee_id: '',
        date_of_year: dateString,
        calendar_week: '',
        status: 'NotLogged',
        daily_hours_booked: 0,
        total_project_hours_booked: 0,
        is_holiday: false,
        is_absence: false,
        is_vacation: false,
        is_bridge: false,
      };
    },
    [statusMap]
  );

  // Calculate week status for each week
  const getWeekStatus = React.useCallback(
    (week: DateValue[]) => {
      // Get calendar week string from the first day of the week (Monday)
      const firstDayOfWeek = week[0].toDate('UTC');
      const calendarWeekStr = getCalendarWeek(firstDayOfWeek);

      // Use weekRecord if available, otherwise calculate from day entries
      const weekStatusFromRecord = weekRecord[calendarWeekStr];

      if (weekStatusFromRecord) {
        // Use the status from weekRecord
        const weekStatus = weekStatusFromRecord === 'close' ? 'close' : 'open';

        return weekStatus;
      }

      // Fallback to original calculation if weekRecord doesn't have this week
      const weekdays = week.slice(0, 5);
      let allComplete = true;
      let weekStatus = 'open';

      for (const day of weekdays) {
        const dateString = format(day.toDate('UTC'), 'yyyy-MM-dd');
        const dayStatus = getStatusForDate(dateString);

        // Skip holidays in week completion check
        if (dayStatus.is_holiday) {
          continue;
        }

        // Skip A/V marked days in week completion check (they count as valid)
        if (dayStatus.is_absence || dayStatus.is_vacation || dayStatus.is_bridge) {
          continue;
        }

        // Skip "NotLogged" status in completion check (they're incomplete by definition)
        if (dayStatus.status === 'NotLogged') {
          allComplete = false;
          break;
        }

        // Check if hours are entered and matching
        if (dayStatus.daily_hours_booked !== dayStatus.total_project_hours_booked) {
          allComplete = false;
          break;
        }

        // Track the week status (close vs draft/approved)
        if (dayStatus.status === 'close') {
          weekStatus = 'close';
        }
      }

      return { allComplete, weekStatus };
    },
    [getStatusForDate, weekRecord]
  );

  const handleDateChange = (details: DatePicker.ValueChangeDetails) => {
    if (details.value && details.value.length > 0) {
      const date = details.value[0].toDate('UTC');
      onDateSelect(date);
      resetHover();
    }
  };

  const value = React.useMemo(() => [parseDate(selectedDate)], [selectedDate]);

  const todayValue = React.useMemo(() => parseDate(new Date()), []);

  // Restore focusedValue when monthlyStatus changes (after query refetch)
  useEffect(() => {
    const savedNav = getSavedNavigation();

    if (!savedNav) return;

    // Restore ref from prop if ref was lost
    if (savedNavigationProp && !lastUserNavigationRef.current) {
      lastUserNavigationRef.current = savedNavigationProp;
    }

    if (!focusedValue) {
      restoreFocusedValue(savedNav);
    } else if (
      isSelectedDateMonth(focusedValue.month, focusedValue.year) &&
      !isSelectedDateMonth(savedNav.month, savedNav.year)
    ) {
      restoreFocusedValue(savedNav);
    }
  }, [
    monthlyStatus,
    focusedValue,
    savedNavigationProp,
    getSavedNavigation,
    restoreFocusedValue,
    isSelectedDateMonth,
  ]);

  // Handle focus changes to track visible month
  const handleFocusChange = (details: { focusedValue: DateValue | undefined }) => {
    if (!details.focusedValue) {
      const savedNav = getSavedNavigation();
      if (savedNav) {
        restoreFocusedValue(savedNav);
      } else {
        setFocusedValue(undefined);
      }
      return;
    }

    const focusedMonth = details.focusedValue.month;
    const focusedYear = details.focusedValue.year;
    const savedNav = getSavedNavigation();

    // Check if this is different from saved navigation (user navigation)
    // This handles both navigating away from selected date AND changing direction
    const isDifferentFromSaved =
      savedNav && (savedNav.month !== focusedMonth || savedNav.year !== focusedYear);
    const isDifferentFromFocused =
      !focusedValue || focusedValue.month !== focusedMonth || focusedValue.year !== focusedYear;

    // KEY FIX: If navigating to a month different from saved navigation, it's ALWAYS user navigation
    // This handles the case where user clicks "prev" to go back to current month (selected date's month)
    // but we have a saved navigation to a different month
    if (isDifferentFromSaved && isDifferentFromFocused) {
      saveNavigation(details.focusedValue);
      setFocusedValue(details.focusedValue);
      return; // Early return - this is user navigation
    }

    // Otherwise, check if it's different from selected date's month
    const isUserNav = !isSelectedDateMonth(focusedMonth, focusedYear);

    if (isUserNav) {
      // User navigation (next or prev) - save it immediately
      saveNavigation(details.focusedValue);
      setFocusedValue(details.focusedValue);
    } else {
      // This might be a reset - preserve the last user navigation if we have one
      if (savedNav && (savedNav.month !== focusedMonth || savedNav.year !== focusedYear)) {
        // Restore the saved navigation immediately
        restoreFocusedValue(savedNav);
        // Use setTimeout to ensure it's set after the DatePicker's internal update
        setTimeout(() => {
          setFocusedValue(savedNav);
        }, 0);
      } else {
        // No saved navigation or it matches, allow it
        setFocusedValue(details.focusedValue);
      }
    }
  };

  return (
    <div
      style={{
        backgroundColor: colors.neutral.white,
        borderRadius: '8px',
        padding: '1rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem',
        }}
      >
        <h3
          style={{
            color: colors.primary.background,
            margin: 0,
            fontSize: '1rem',
            fontWeight: '600',
          }}
        >
          Date Navigation
        </h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {onOpenExternalPresence && (
            <button
              type="button"
              onClick={onOpenExternalPresence}
              style={{
                padding: '8px 16px',
                backgroundColor: colors.neutral.white,
                color: colors.accent.blue,
                border: `1px solid ${colors.accent.blue}`,
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <MapPin size={16} color={colors.accent.blue} />
              Presence
            </button>
          )}
          {onOpenAbsenceVacationDialog && (
            <button
              type="button"
              onClick={onOpenAbsenceVacationDialog}
              style={{
                padding: '8px 16px',
                backgroundColor: colors.neutral.white,
                color: colors.pastel.brownDark,
                border: `1px solid ${colors.pastel.brownDark}`,
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <CalendarX size={16} color={colors.pastel.brownDark} />
              Bridge Days
            </button>
          )}
        </div>
      </div>
      <DatePicker.Root
        open
        fixedWeeks
        startOfWeek={1}
        selectionMode="single"
        value={value}
        onValueChange={handleDateChange}
        focusedValue={focusedValue}
        onFocusChange={handleFocusChange}
        style={{ width: '100%' }}
      >
        <DatePicker.Content
          style={{
            backgroundColor: colors.neutral.white,
            border: `1px solid ${colors.neutral.grey[300]}`,
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            padding: '0.75rem',
            marginTop: '0.5rem',
          }}
        >
          <DatePicker.View view="day">
            <DatePicker.Context>
              {(datePicker) => {
                const visibleStartMonth = datePicker.visibleRange.start.month;
                const visibleStartYear = datePicker.visibleRange.start.year;

                // Use useLayoutEffect to restore focusedValue synchronously before paint
                // This ensures the calendar view is preserved when navigating (next or prev)
                useLayoutEffect(() => {
                  const savedNav = getSavedNavigation();
                  if (!savedNav) return;

                  // Restore ref from prop if ref was lost
                  if (savedNavigationProp && !lastUserNavigationRef.current) {
                    lastUserNavigationRef.current = savedNavigationProp;
                  }

                  const isVisibleReset = isSelectedDateMonth(visibleStartMonth, visibleStartYear);
                  const savedMonth = savedNav.month;
                  const savedYear = savedNav.year;

                  // If visible month reset to selected date's month but we have a saved navigation, restore it
                  if (isVisibleReset && !isSelectedDateMonth(savedMonth, savedYear)) {
                    restoreFocusedValue(savedNav);
                    return;
                  }

                  // Only restore if focusedValue doesn't match visible month
                  // This allows new navigation (next/prev) to be saved without interference
                  const visibleMatchesFocused =
                    focusedValue &&
                    focusedValue.month === visibleStartMonth &&
                    focusedValue.year === visibleStartYear;

                  // If visible month matches saved navigation but focusedValue doesn't, restore it
                  // But only if focusedValue doesn't already match the visible month (new navigation)
                  if (visibleStartMonth === savedMonth && visibleStartYear === savedYear) {
                    if (
                      !visibleMatchesFocused &&
                      (!focusedValue ||
                        focusedValue.month !== savedMonth ||
                        focusedValue.year !== savedYear)
                    ) {
                      restoreFocusedValue(savedNav);
                    }
                    return;
                  }

                  // If focusedValue is undefined or reset to selected date's month, restore saved navigation
                  if (!focusedValue) {
                    restoreFocusedValue(savedNav);
                  } else if (
                    isSelectedDateMonth(focusedValue.month, focusedValue.year) &&
                    !isSelectedDateMonth(savedMonth, savedYear)
                  ) {
                    restoreFocusedValue(savedNav);
                  }
                }, [
                  visibleStartMonth,
                  visibleStartYear,
                  selectedDate,
                  focusedValue,
                  monthlyStatus,
                  savedNavigationProp,
                  getSavedNavigation,
                  isSelectedDateMonth,
                  restoreFocusedValue,
                ]);

                // Sync from DatePicker context only for genuine user navigation
                // This ensures new navigation (next/prev) is captured and saved
                useEffect(() => {
                  const currentFocused = datePicker.focusedValue;
                  if (!currentFocused) return;

                  const isUserNav = !isSelectedDateMonth(currentFocused.month, currentFocused.year);

                  if (isUserNav) {
                    // Always save new user navigation, even if it's different from current focusedValue
                    // This ensures both next and prev navigation work correctly
                    const shouldSave =
                      currentFocused.month !== focusedValue?.month ||
                      currentFocused.year !== focusedValue?.year;
                    if (shouldSave) {
                      saveNavigation(currentFocused);
                      setFocusedValue(currentFocused);
                    }
                  } else {
                    // Only restore if current focused doesn't match visible month (not a new navigation)
                    const visibleMatchesFocused =
                      focusedValue &&
                      focusedValue.month === visibleStartMonth &&
                      focusedValue.year === visibleStartYear;

                    if (!visibleMatchesFocused) {
                      const savedNav = getSavedNavigation();
                      if (
                        savedNav &&
                        (focusedValue?.month !== savedNav.month ||
                          focusedValue?.year !== savedNav.year)
                      ) {
                        restoreFocusedValue(savedNav);
                      }
                    }
                  }
                }, [
                  visibleStartMonth,
                  visibleStartYear,
                  selectedDate,
                  focusedValue,
                  datePicker,
                  isSelectedDateMonth,
                  saveNavigation,
                  getSavedNavigation,
                  restoreFocusedValue,
                ]);

                return (
                  <>
                    <MonthChangeWatcher
                      visibleStartMonth={visibleStartMonth}
                      visibleStartYear={visibleStartYear}
                      onMonthChange={onMonthChange}
                    />
                    <CalendarHeader
                      onSelectToday={() => {
                        datePicker.selectToday();
                        onDateSelect(new Date());
                      }}
                    />

                    <DatePicker.Table
                      style={{
                        width: '100%',
                        borderCollapse: 'separate',
                        borderSpacing: '0 4px',
                      }}
                    >
                      <DatePicker.TableHead>
                        <DatePicker.TableRow>
                          <DatePicker.TableHeader
                            style={{
                              textAlign: 'center',
                              fontSize: '0.65rem',
                              fontWeight: '600',
                              color: colors.secondary.text,
                              padding: '0.25rem 0',
                              border: 'none',
                              width: '2rem',
                            }}
                          >
                            CW
                          </DatePicker.TableHeader>
                          {datePicker.weekDays.map((weekDay, id) => (
                            <DatePicker.TableHeader
                              key={id}
                              style={{
                                textAlign: 'center',
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                color: colors.secondary.text,
                                padding: '0.25rem 0',
                                border: 'none',
                                maxWidth: '2.5rem',
                              }}
                            >
                              {weekDay.short}
                            </DatePicker.TableHeader>
                          ))}
                        </DatePicker.TableRow>
                      </DatePicker.TableHead>
                      <DatePicker.TableBody>
                        {datePicker.weeks.map((week, id) => {
                          const weekStatus = getWeekStatus(week);
                          // Use weekStatus from weekRecord for coloring: "close" = green, "open" = yellow (if complete) or grey (if incomplete)
                          const weekBgColor =
                            weekStatus === 'close'
                              ? colors.pastel.greenWeek
                              : colors.pastel.yellowWeek;
                          const weekVisible = week.some(
                            (d) => d.month === datePicker.visibleRange.start.month
                          );

                          return (
                            <DatePicker.TableRow key={id}>
                              {/* Week Number Column */}
                              <td
                                key={id}
                                style={{
                                  textAlign: 'center',
                                  padding: '0.15rem',
                                  fontSize: '0.7rem',
                                  fontWeight: '600',
                                  color: colors.secondary.text,
                                  backgroundColor: weekVisible ? weekBgColor : 'transparent',
                                  width: '2rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: '2.5rem',
                                  transition: 'background-color 0.2s ease',
                                  borderRadius: '0.1rem',
                                }}
                              >
                                {weekVisible ? getIsoWeek(week[0]) : null}
                              </td>
                              {/* Days of the week */}
                              {week.map((day, dayId) => {
                                const isToday = todayValue.compare(day) === 0;
                                const isSelected = datePicker.value?.[0]?.compare(day) === 0;
                                const isWeekend = dayId === 5 || dayId === 6;
                                const isCurrentMonth =
                                  datePicker.visibleRange.start.month === day.month;
                                const isHovered = hoveredDate && day.compare(hoveredDate) === 0;
                                const dateString = format(day.toDate('UTC'), 'yyyy-MM-dd');
                                const statusForDay = getStatusForDate(dateString);

                                // Check if this date is a holiday (from monthlyStatus)
                                const isHoliday = statusForDay.is_holiday ?? false;

                                // Check if this date is in the past
                                const isPast =
                                  day.toDate('UTC') < new Date(new Date().setHours(0, 0, 0, 0));

                                // Get actual and project hours from API data
                                const actualHours = statusForDay.daily_hours_booked ?? 0;
                                const projectHours = statusForDay.total_project_hours_booked ?? 0;

                                // Determine status - use API status or default to 'draft'
                                const cellStatus = statusForDay.status ?? 'NotLogged';

                                // Get absence, vacation, and bridge flags (ensure they are booleans)
                                const isAbsence = Boolean(statusForDay.is_absence ?? false);
                                const isVacation = Boolean(statusForDay.is_vacation ?? false);
                                const isBridge = Boolean(statusForDay.is_bridge ?? false);

                                return (
                                  <CalendarCell
                                    key={dayId}
                                    day={day}
                                    isToday={isToday}
                                    isSelected={isSelected}
                                    isWeekend={isWeekend}
                                    isCurrentMonth={isCurrentMonth}
                                    isHovered={isHovered || false}
                                    onMouseEnter={() =>
                                      handleDateHover(day, isCurrentMonth, isWeekend, isSelected)
                                    }
                                    onMouseLeave={handleDateLeave}
                                    status={cellStatus}
                                    isHoliday={isHoliday}
                                    isPast={isPast}
                                    actualHours={actualHours}
                                    projectHours={projectHours}
                                    isAbsence={isAbsence}
                                    isVacation={isVacation}
                                    isBridge={isBridge}
                                  />
                                );
                              })}
                            </DatePicker.TableRow>
                          );
                        })}
                      </DatePicker.TableBody>
                    </DatePicker.Table>
                  </>
                );
              }}
            </DatePicker.Context>
          </DatePicker.View>
        </DatePicker.Content>
      </DatePicker.Root>

      <CalendarLegend />
    </div>
  );
};
